import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect } from "react";

export const DEFAULT_COMMUNITY_TABS = [
  { id: 'all', icon: 'Clock', label: 'חדש' },
  { id: 'popular', icon: 'Flame', label: 'פופולרי' },
  { id: 'my_posts', icon: 'UserIcon', label: 'פוסטים שלי', requiresAuth: true },
  { id: 'systems', icon: 'AppWindow', label: 'מערכות חדשות' },
  { id: '#שאלה', icon: 'Hash', label: 'שאלות' },
  { id: '#חפש-שותף', icon: 'Hash', label: 'חיפוש שותפים' },
];

export const useCommunitySettings = () => {
  return useQuery({
    queryKey: ['community-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_settings')
        .select('value')
        .eq('key', 'feed_enabled')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      const val = data?.value as any;
      return { 
        enabled: val?.enabled ?? true, 
        auto_posts: val?.auto_posts ?? true,
        tabs: val?.tabs || DEFAULT_COMMUNITY_TABS
      };
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSinglePost = (postId: string) => {
  return useQuery({
    queryKey: ['community-post', postId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id;

      const { data, error } = await supabase
        .from('community_posts')
        .select(`
          *,
          user:profiles!community_posts_user_id_fkey(display_name, role),
          linked_system:systems(id, title, slug, short_desc, system_images(public_url, is_cover)),
          likes:community_likes(user_id)
        `)
        .eq('id', postId)
        .single();

      if (error) throw error;

      return {
        ...data,
        isLikedByMe: currentUserId ? data.likes.some((l: any) => l.user_id === currentUserId) : false,
      };
    },
    enabled: !!postId,
  });
};

export const useCommunityPosts = (filter: 'all' | 'popular' | 'systems' | 'my_posts' | string) => {
  return useInfiniteQuery({
    queryKey: ['community-posts', filter],
    queryFn: async ({ pageParam = 0 }) => {
      try {
        const { data: { session }, error: authError } = await supabase.auth.getSession();
        if (authError) {
          console.error("[useCommunityPosts] Auth session error:", authError);
        }
        
        const currentUserId = session?.user?.id;

        let query = supabase
          .from('community_posts')
          .select(`
            *,
            user:profiles!community_posts_user_id_fkey(display_name, role),
            linked_system:systems(id, title, slug, short_desc, system_images(public_url, is_cover)),
            likes:community_likes(user_id)
          `);

        if (filter === 'my_posts' && currentUserId) {
          query = query.eq('user_id', currentUserId);
        } else {
          query = query.eq('status', 'active');
        }

        if (filter === 'popular') {
          query = query.order('likes_count', { ascending: false }).order('created_at', { ascending: false });
        } else if (filter === 'systems') {
          query = query.eq('post_type', 'system_listing').order('created_at', { ascending: false });
        } else if (filter === 'my_posts') {
          query = query.order('created_at', { ascending: false });
        } else if (filter !== 'all') {
          query = query.contains('tags', [filter]).order('created_at', { ascending: false });
        } else {
          query = query.order('is_pinned', { ascending: false }).order('created_at', { ascending: false });
        }

        const { data, error } = await query.range(pageParam, pageParam + 19);
        
        if (error) {
          console.error("[useCommunityPosts] Query failed:", error);
          throw error;
        }

        if (!data) {
          return { items: [], nextCursor: undefined };
        }

        const processedData = data.map((post: any) => ({
          ...post,
          isLikedByMe: currentUserId ? post.likes.some((l: any) => l.user_id === currentUserId) : false,
        }));

        return {
          items: processedData,
          nextCursor: data.length === 20 ? pageParam + 20 : undefined,
        };
      } catch (err) {
        console.error("[useCommunityPosts] Fatal error in queryFn:", err);
        throw err;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
  });
};

export const useCommunityActions = () => {
  const queryClient = useQueryClient();

  const createPost = useMutation({
    mutationFn: async ({ content, tags, imageUrl }: { content: string; tags: string[]; imageUrl?: string | null }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("יש להתחבר כדי לפרסם");

      const sanitizedContent = content.replace(/<[^>]*>/g, '').trim();
      if (!sanitizedContent && !imageUrl) throw new Error("תוכן לא חוקי");

      const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
      const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';

      const { error } = await supabase.from('community_posts').insert({
        user_id: user.id,
        content: sanitizedContent,
        tags,
        image_url: imageUrl,
        status: isAdmin ? 'active' : 'pending'
      } as any);
      
      if (error) {
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error("הגעת למגבלת פרסום הפוסטים. אנא המתן מספר דקות.");
        }
        throw error;
      }
      
      return { isAdmin };
    },
    onSuccess: (data) => {
      if (data.isAdmin) {
        toast.success("הפוסט פורסם בהצלחה!");
      } else {
        toast.success("הפוסט נשלח לאישור מנהל ויפורסם בקרוב!");
      }
      queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pending-posts'] });
    },
    onError: (err: any) => toast.error(err.message || "שגיאה בפרסום הפוסט"),
  });

  const editPost = useMutation({
    mutationFn: async ({ postId, content, tags }: { postId: string; content: string; tags: string[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("יש להתחבר כדי לערוך");

      const sanitizedContent = content.replace(/<[^>]*>/g, '').trim();
      if (!sanitizedContent) throw new Error("תוכן לא חוקי");

      const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
      const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';

      const { error } = await supabase.from('community_posts')
        .update({
          content: sanitizedContent,
          tags,
          status: isAdmin ? 'active' : 'pending' 
        })
        .eq('id', postId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      return { isAdmin };
    },
    onSuccess: (data) => {
      if (data.isAdmin) {
        toast.success("הפוסט עודכן בהצלחה!");
      } else {
        toast.success("הפוסט נשלח לבדיקה מחודשת אצל מנהל המערכת");
      }
      queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pending-posts'] });
      queryClient.invalidateQueries({ queryKey: ['community-post'] });
    },
    onError: (err: any) => toast.error(err.message || "שגיאה בעדכון הפוסט"),
  });

  const deletePost = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase.from('community_posts').delete().eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("הפוסט נמחק בהצלחה");
      queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      queryClient.invalidateQueries({ queryKey: ['community-post'] }); 
    },
    onError: (err: any) => toast.error("שגיאה במחיקת הפוסט")
  });

  const toggleLike = useMutation({
    mutationFn: async ({ postId, isLiked }: { postId: string; isLiked: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("יש להתחבר כדי לתת לייק");

      if (isLiked) {
        await supabase.from('community_likes').delete().eq('post_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('community_likes').insert({ post_id: postId, user_id: user.id } as any);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      queryClient.invalidateQueries({ queryKey: ['community-post'] });
    },
    onError: (err: any) => toast.error(err.message || "שגיאה"),
  });

  const addComment = useMutation({
    mutationFn: async ({ postId, content, parentId, replyToUserId }: { postId: string; content: string; parentId?: string; replyToUserId?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("יש להתחבר כדי להגיב");

      const sanitizedContent = content.replace(/<[^>]*>/g, '').trim();

      const payload: any = {
        post_id: postId,
        user_id: user.id,
        content: sanitizedContent,
      };

      if (parentId) payload.parent_id = parentId;
      if (replyToUserId) payload.reply_to_user_id = replyToUserId;

      const { error } = await supabase.from('community_comments').insert(payload);
      if (error) {
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error("הגעת למגבלת התגובות. אנא המתן מספר דקות.");
        }
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['community-comments', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      queryClient.invalidateQueries({ queryKey: ['community-post'] });
      toast.success("התגובה פורסמה בהצלחה");
    },
    onError: (err: any) => toast.error(err.message || "שגיאה בפרסום התגובה"),
  });

  return { createPost, editPost, deletePost, toggleLike, addComment };
};

export const usePostComments = (postId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['community-comments', postId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_comments')
        .select('*, user:profiles!community_comments_user_id_fkey(display_name)')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!postId,
  });
};

export const usePendingCommunityPosts = () => {
  return useQuery({
    queryKey: ['admin-pending-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_posts')
        .select('*, user:profiles!community_posts_user_id_fkey(display_name)')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });
};

export const useAdminCommunityActions = () => {
  const queryClient = useQueryClient();
  
  const approvePost = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase.from('community_posts').update({ status: 'active' }).eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("הפוסט אושר ופורסם בפיד");
      queryClient.invalidateQueries({ queryKey: ['admin-pending-posts'] });
      queryClient.invalidateQueries({ queryKey: ['community-posts'] });
    },
    onError: () => toast.error("שגיאה באישור הפוסט")
  });

  const rejectPost = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase.from('community_posts').delete().eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("הפוסט נדחה ונמחק");
      queryClient.invalidateQueries({ queryKey: ['admin-pending-posts'] });
    },
    onError: () => toast.error("שגיאה בדחיית הפוסט")
  });

  return { approvePost, rejectPost };
};